import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@ncoder/ui';
import { Shield, Workflow, Bot, Building2, Rocket, Database, FileText, CalendarClock, Network } from 'lucide-react';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrg } from '@/hooks/org/useOrg';
import { useWorkflows } from '@/hooks/workflow/useWorkflows';
import { useAgents } from '@/hooks/agent/useAgents';
import { useSecurityHealth } from '@/hooks/security-scanner/useSecurityHealth';

// Stat Card Skeleton Component
const StatCardSkeleton: React.FC = () => (
  <div className="rounded-lg border bg-card p-6 shadow-sm">
    <div className="flex items-center">
      <Skeleton className="h-4 w-4 rounded" />
      <Skeleton className="ml-2 h-4 w-24" />
    </div>
    <div className="mt-2">
      <Skeleton className="h-8 w-16 mb-2" />
      <Skeleton className="h-3 w-32" />
    </div>
  </div>
);

function getRelativeTime(timestamp: string): string {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organizations, selectedOrg } = useOrg();

  // Fetch workflows data (parallel API call with default query key)
  const { data: workflowsData, isLoading: isLoadingWorkflows } = useWorkflows({
    user_id: user?.id as string,
    page: 1,
    page_size: 10, // Fetch recent workflows for activity feed
  });

  // Fetch agents data (parallel API call with default query key)
  const { data: agentsData, isLoading: isLoadingAgents } = useAgents({
    user_id: user?.id as string,
    limit: 10, // Fetch recent agents for activity feed
    offset: 0,
  });

  // Fetch security health data (parallel API call with default query key)
  const { data: securityHealthResponse, isLoading: isLoadingSecurity } = useSecurityHealth({
    org_id: selectedOrg?.id,
    user_id: user?.id,
  });

  // Extract data from API responses
  const totalWorkflows = workflowsData?.pagination?.total_entries || 0;
  const totalAgents = agentsData?.pagination?.total_entries || 0;
  const totalOrgs = organizations?.length || 0;
  const securityScore = securityHealthResponse?.security_health?.overall_security_score 
    ? securityHealthResponse.security_health.overall_security_score.toFixed(1)
    : '0.0';

  // Combine recent activities from workflows and agents
  const recentActivities = React.useMemo(() => {
    const activities: Array<{
      type: 'workflow' | 'agent';
      action: string;
      name: string;
      timestamp: string;
      time: string;
    }> = [];

    // Add workflow activities
    if (workflowsData?.workflows) {
      workflowsData.workflows.forEach((workflow) => {
        const timestamp = workflow.updated_at || workflow.inserted_at || workflow.created_at;
        if (timestamp) {
          activities.push({
            type: 'workflow',
            action: workflow.updated_at ? 'Updated workflow' : 'Created workflow',
            name: workflow.name,
            timestamp,
            time: getRelativeTime(timestamp),
          });
        }
      });
    }

    // Add agent activities
    if (agentsData?.agents) {
      agentsData.agents.forEach((agent) => {
        const timestamp = agent.updated_at || agent.inserted_at || agent.created_at;
        if (timestamp) {
          activities.push({
            type: 'agent',
            action: agent.updated_at ? 'Updated agent' : 'Created agent',
            name: agent.name,
            timestamp,
            time: getRelativeTime(timestamp),
          });
        }
      });
    }

    // Sort by timestamp (most recent first) and take top 5
    return activities
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 5);
  }, [workflowsData, agentsData]);

  const stats = [
    {
      title: 'Total Migrations',
      value: totalWorkflows.toString(),
      subtitle: 'Active migrations',
      icon: Workflow,
      isLoading: isLoadingWorkflows,
    },
    {
      title: 'Total Agents',
      value: totalAgents.toString(),
      subtitle: 'Configured agents',
      icon: Bot,
      isLoading: isLoadingAgents,
    },
    {
      title: 'Connected Orgs',
      value: totalOrgs.toString(),
      subtitle: 'Salesforce organizations',
      icon: Building2,
      isLoading: false, // From state, not API
    },
    {
      title: 'Security Score',
      value: `${securityScore}%`,
      subtitle: 'Overall security health',
      icon: Shield,
      isLoading: isLoadingSecurity,
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <section className="studio-hero">
        <div>
          <div className="studio-eyebrow">Home</div>
          <h1 className="studio-title">
            Start with the selected <em>org context.</em>
          </h1>
          <p className="studio-subtitle">
            Review connected orgs, active automations, security health, and the most useful Studio actions from one place.
          </p>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          stat.isLoading ? (
            <StatCardSkeleton key={stat.title} />
          ) : (
            <div
              key={stat.title}
              className="studio-card studio-card-hover p-5"
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-xs font-semibold uppercase text-muted-foreground">{stat.title}</h3>
                <span className="rounded-md bg-muted p-2 text-foreground">
                  <stat.icon className="h-4 w-4" />
                </span>
              </div>
              <div className="text-3xl font-bold">{stat.value}</div>
              <p className="mt-1 text-sm text-muted-foreground">{stat.subtitle}</p>
            </div>
          )
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-7">
        <div className="studio-card p-5 md:col-span-4">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold">Recent activity</h3>
              <p className="text-sm text-muted-foreground">Latest workflow and agent changes.</p>
            </div>
          </div>
          <div className="space-y-4">
            {isLoadingWorkflows || isLoadingAgents ? (
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <Skeleton className="h-2 w-2 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))
            ) : recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div 
                    className={`flex h-2 w-2 rounded-full ${
                      activity.type === 'workflow' ? 'bg-blue-600' : 'bg-purple-600'
                    }`} 
                  />
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-sm font-medium leading-none truncate">
                      {activity.action}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {activity.name} • {activity.time}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No recent activity</p>
                <p className="text-xs mt-1">Run a migration or set up a schedule to see activity here.</p>
              </div>
            )}
          </div>
        </div>

        <div className="studio-card p-5 md:col-span-3">
          <h3 className="text-lg font-semibold">Quick actions</h3>
          <p className="mt-1 text-sm text-muted-foreground">Open the most common Studio workflows.</p>
          <div className="mt-4 grid gap-2">
            <ActionButton icon={<Database className="h-4 w-4" />} label="Metadata Explorer" onClick={() => navigate('/metadata')} />
            <ActionButton icon={<FileText className="h-4 w-4" />} label="Run Migrations" onClick={() => navigate('/migrations')} />
            <ActionButton icon={<Bot className="h-4 w-4" />} label="Agents" onClick={() => navigate('/agents')} />
            <ActionButton icon={<CalendarClock className="h-4 w-4" />} label="Scheduling" onClick={() => navigate('/scheduling')} />
            <ActionButton icon={<Rocket className="h-4 w-4" />} label="Deployment" onClick={() => navigate('/deployment')} />
            <ActionButton icon={<Network className="h-4 w-4" />} label="Weave" onClick={() => navigate('/weave')} />
          </div>
        </div>
      </div>
    </div>
  );
};

function ActionButton({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-11 items-center justify-between gap-3 rounded-md border border-border bg-card px-3 text-sm font-semibold transition-colors hover:bg-muted"
    >
      <span className="inline-flex items-center gap-2">
        {icon}
        {label}
      </span>
      <span className="text-muted-foreground">Open</span>
    </button>
  );
}
